'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Script from 'next/script'
import Button from '@/components/ui/Button'
import { TURNSTILE_SCRIPT, useTurnstile } from '@/components/tools/runs/useTurnstile'
import { INDUSTRY_MAX, LOCATION_MAX, MAX_COMPETITORS } from '@/lib/backlink-strategy/types'

type CreateResponse =
  | { ok: true; id: string; status: string; cached: boolean }
  | { ok: false; error: string; message?: string; retryAfterSeconds?: number }

const FIELD =
  'mt-2 w-full rounded-md border bg-bg px-3 py-2.5 text-fg outline-none transition-colors focus:border-accent/60'

/** Target domain, optional competitors, optional industry and location. */
export default function MatrixForm() {
  const router = useRouter()
  const [domain, setDomain] = useState('')
  const [competitors, setCompetitors] = useState('')
  const [industry, setIndustry] = useState('')
  const [location, setLocation] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const turnstile = useTurnstile()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (pending) return
    setError(null)
    if (!domain.trim()) {
      setError('Enter the domain you want links for.')
      return
    }
    if (turnstile.enabled && !turnstile.token) {
      setError('The anti-bot check is still running. Give it a second and try again.')
      return
    }
    setPending(true)
    try {
      const res = await fetch('/api/tools/runs/backlink-strategy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain,
          competitors: competitors
            .split(/[\n,]+/)
            .map((s) => s.trim())
            .filter(Boolean),
          industry: industry.trim() || undefined,
          location: location.trim() || undefined,
          turnstileToken: turnstile.token ?? undefined,
        }),
      })
      const data = (await res.json()) as CreateResponse
      if (!data.ok) {
        setError(data.message ?? 'We could not start the build. Try again.')
        if (data.error === 'captcha_failed') turnstile.reset()
        setPending(false)
        return
      }
      router.push(`/tools/backlink-strategy-matrix/report/${data.id}`)
    } catch {
      setError('We could not reach the service. Check your connection and try again.')
      setPending(false)
    }
  }

  return (
    <form onSubmit={submit} className="card mt-10 p-6 sm:p-8" noValidate>
      {turnstile.enabled && (
        <Script src={TURNSTILE_SCRIPT} strategy="afterInteractive" onLoad={turnstile.render} />
      )}
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block" htmlFor="matrix-domain">
          <span className="text-sm font-medium text-fg">Your domain</span>
          <input
            id="matrix-domain"
            type="text"
            inputMode="url"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
            autoComplete="url"
            spellCheck={false}
            aria-invalid={error ? true : undefined}
            aria-describedby="matrix-domain-hint"
            className={`${FIELD} ${error ? 'border-danger/50' : 'border-line'}`}
          />
          <span id="matrix-domain-hint" className="mt-1 block text-xs text-muted">
            We read the homepage once for what the business is. Nothing else is fetched.
          </span>
        </label>
        <label className="block" htmlFor="matrix-competitors">
          <span className="text-sm font-medium text-fg">
            Competitors{' '}
            <span className="font-normal text-muted">(optional, up to {MAX_COMPETITORS})</span>
          </span>
          <textarea
            id="matrix-competitors"
            value={competitors}
            onChange={(e) => setCompetitors(e.target.value)}
            placeholder={'rival-one.com\nrival-two.com'}
            rows={2}
            spellCheck={false}
            className={`${FIELD} border-line resize-y`}
          />
          <span className="mt-1 block text-xs text-muted">
            One per line. Their homepages are read for positioning only; nobody looks up their
            links.
          </span>
        </label>
        <label className="block" htmlFor="matrix-industry">
          <span className="text-sm font-medium text-fg">
            Industry <span className="font-normal text-muted">(optional)</span>
          </span>
          <input
            id="matrix-industry"
            type="text"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="e.g. independent car dealer"
            maxLength={INDUSTRY_MAX}
            autoComplete="off"
            className={`${FIELD} border-line`}
          />
        </label>
        <label className="block" htmlFor="matrix-location">
          <span className="text-sm font-medium text-fg">
            Service area <span className="font-normal text-muted">(optional)</span>
          </span>
          <input
            id="matrix-location"
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. Atlanta, GA"
            maxLength={LOCATION_MAX}
            autoComplete="off"
            className={`${FIELD} border-line`}
          />
          <span className="mt-1 block text-xs text-muted">
            Turns on local sources: press, chambers, city guides, sponsorships.
          </span>
        </label>
      </div>
      <div ref={turnstile.widgetRef} className="mt-4" />
      {error && (
        <p role="alert" className="mt-4 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Button type="submit" size="lg" disabled={pending || !turnstile.ready}>
          {pending ? 'Starting the build' : 'Build the matrix'}
        </Button>
        <span className="text-sm text-muted">Free. No email required.</span>
      </div>
      <p className="mt-6 text-sm leading-relaxed text-muted">
        What happens: we read the homepages, the model writes the link-source categories a business
        like yours should be earning from, with a tactic, an angle and example domains for each, and
        every example domain is checked against DNS before it is shown. No backlink index is
        consulted, and the report says so.
      </p>
    </form>
  )
}
